import React, { useState, useEffect } from 'react'
import { gql } from '@apollo/client'
import PubSub from 'pubsub-js'
import AppObject from '../AppObject/AppObject'
import { Button, Alert, Spinner, UncontrolledTooltip } from 'reactstrap'
import { ACTION_TYPE, FIELD_TYPE, FORMAT_TYPE, ATTRIBUTE_NAME } from '../../constants.js'
import { OBJECT_QUERY } from '../../graphql'
import { hasActionType, getActionOfType, makeInteger, getAlignment, createInitialFormInput, getHookInfo, getAttributeValue } from '../../utils'
import FileLink from '../FileLink/FileLink'
import allHooks from '../../hooks/allHooks'

export const OBJECT = gql(OBJECT_QUERY)

export default function RefListRow(props) {
  const { details, gridRow, formInputs, editButtonClicked, removeRow, parentObjectId, parentAction, colSpan, userActions, modifyRefListChangeCount, fileJwt, printView } = props
  //console.log(`RefListRow ${gridRow[0]} rendered: `, props)

  const [showObject, setShowObject] = useState(false)
  const [deleted, setDeleted] = useState(false)
  const [rowAction, setRowAction] = useState()
  const [refListObjectId] = useState(gridRow[0])
  const [graphQLErrors, setGraphQLErrors] = useState([])
  const [retrieving, setRetrieving] = useState(false)
  const showObjectId = getAttributeValue(ATTRIBUTE_NAME.showObjectId)

  useEffect(() => {
    function handleCloseButton(msg, data) {
      console.log(`EVENT RECEIVED (RefListRow): ${msg}`, data)
      setShowObject(false)
      setRowAction(null)
    }
    const eventName = `CLOSE BUTTON ${refListObjectId}`
    const token = PubSub.subscribe(eventName, handleCloseButton)
    return function cleanup() {
      PubSub.unsubscribe(token)
    }
  })

  function getObjectFromList() {
    for (let i = 0; i < formInputs.objects.length; i++) {
      if (makeInteger(formInputs.objects[i].id) === makeInteger(refListObjectId)) {
        return formInputs.objects[i]
      }
    }
    return null
  }

  async function getRefListObject() {
    console.log(`RefListRow.getRefListObject: ${refListObjectId}`)
    let refListObject = getObjectFromList()
    if (refListObject) {
      return refListObject
    } else {
      setRetrieving(true)
      const { data, errors } = await window.apolloClient.query({ query: OBJECT, variables: { id: makeInteger(refListObjectId) } })
      if (data.object) {
        //console.log('RefListRow.getRefListObject: Object: ', data.object)
        formInputs.objects.push(data.object)
        const refListObjectFormInput = createInitialFormInput(data.object, parentAction)
        //console.log('RefListRow.getRefListObject intialFormInput', refListObjectFormInput)
        formInputs[refListObjectId] = refListObjectFormInput
        setRetrieving(false)
        return data.object
      }
      setRetrieving(false)
      if (errors) {
        console.log('RefListRow.getRefListObject errors', errors)
        setGraphQLErrors(errors)
      }
    }
  }

  async function viewButtonClicked() {
    console.log('RefListRow.viewButtonClicked', refListObjectId)
    await getRefListObject()
    setShowObject(true)
  }

  async function deleteButtonClicked() {
    console.log('RefListRow.deleteButtonClicked', formInputs)
    let theRefListObject = await getRefListObject()
    if (theRefListObject.submitting) {
      // tell the ReferenceList to delete this item
      removeRow(theRefListObject.id)
    } else {
      const delta = deleted ? -1 : 1
      if (!deleted) {
        const deleteAction = getActionOfType(userActions, ACTION_TYPE.delete)
        formInputs[theRefListObject.id].action = deleteAction
      } else {
        delete formInputs[theRefListObject.id].action
      }
      setDeleted(!deleted)
      // update the refList changeCount
      modifyRefListChangeCount(delta)
    }
  }

  async function localEditButtonClicked() {
    console.log('RefListRow.localEditButtonClickied', refListObjectId)
    let theRefListObject = await getRefListObject()
    editButtonClicked(theRefListObject)
  }

  let buttons = ''
  if (retrieving) {
    buttons = <Spinner size="sm" color="primary" />
  } else {
    buttons = <Button color="secondary" size="sm" onClick={viewButtonClicked}>View</Button>
    if (parentAction) {
      let editButton = ''
      let deleteButton = ''
      if (hasActionType(userActions, ACTION_TYPE.modify) && !deleted) {
        let dataCy = ''
        if (gridRow[1] !== undefined) {
          if (typeof gridRow[1] === 'string') {
            dataCy = `-${gridRow[1].toLowerCase().replace(/ /g, '-')}`
          } else {
            dataCy = `-${gridRow[1]}`
          }
        }
        editButton = <Button data-cy={`ref-list-edit-button${dataCy}`} className="margin-right-5" color="warning" size="sm" onClick={localEditButtonClicked}>Edit</Button>
      }
      if (hasActionType(userActions, ACTION_TYPE.delete)) {
        let buttonText = 'Delete'
        let buttonColor = 'danger'
        let buttonClassName = ''
        if (deleted) {
          buttonText = 'Undelete'
          buttonColor = 'success'
          buttonClassName = 'ref-list-row-undelete-button'
        }
        deleteButton = <Button className={buttonClassName} color={buttonColor} size="sm" onClick={deleteButtonClicked}>{buttonText}</Button>
      }
      buttons = 
        <React.Fragment>
          {editButton}
          {deleteButton}
        </React.Fragment>
    }
  }

  const rowColumns = []
  for (let columnIndex = 0; columnIndex < details.length; columnIndex++) {
    //console.log(`^^^^^^^^^^^^^^^^^`^^^^^^ columnIndex: ${columnIndex}`, details, gridRow)
    let columnContent = ''
    let comma = null
    let detailField = details[columnIndex].field
    let detailFieldId = detailField.id
    let fieldTypeId = detailField.type.id
    let className = getAlignment(fieldTypeId)
    let formInputCurrentValue = null
    let rowObject = null

    // if the row object has been retrieved
    if (formInputs && (formInputs[refListObjectId] !== undefined) && (formInputs[refListObjectId][detailFieldId] !== undefined)) {
      rowObject = formInputs[refListObjectId]
      formInputCurrentValue = formInputs[refListObjectId][detailFieldId].currentValue
      if (formInputs[refListObjectId][detailFieldId].changed) {
        className += ' changed'
      }
    // otherwise use the gridRow data
    } else {
      if (makeInteger(fieldTypeId) === FIELD_TYPE.file) {
        columnContent = <FileLink fileName={gridRow[columnIndex + 1]} jwt={fileJwt} />
      } else if (makeInteger(fieldTypeId) === FIELD_TYPE.referenceList) {
        //const newFormInputs = { field: detailField, savedValue: {}, currentValue: {}, changed: false }
        //columnContent = <ReferenceList formInputs={newFormInputs} objectId={gridRow[0]} field={detailField} action={parentAction} />
        columnContent = 'REFERENCE LIST'
      } else {
        columnContent = gridRow[columnIndex + 1]
      }
    }

    if (formInputCurrentValue || formInputCurrentValue === 0) {
      console.log(`RefListRow columnIndex: ${columnIndex} formInputCurrentValue: ${formInputCurrentValue}`, fieldTypeId)
      switch (makeInteger(fieldTypeId)) {
        case FIELD_TYPE.filterValue:
          if (Array.isArray(formInputCurrentValue)) {
            comma = ''
            for (let choice of formInputCurrentValue) {
              columnContent += `${comma}${choice.name}`
              comma = ', '
            }
          } else {
            if (formInputCurrentValue && formInputCurrentValue.name) {
              columnContent = formInputCurrentValue.name
            } else if (Object.keys(formInputCurrentValue).length !== 0) {
              columnContent = formInputCurrentValue
            } else {
              columnContent = ''
            }
          }
          //console.log(columnContent)
          break
        case FIELD_TYPE.select:
          if (formInputCurrentValue) {
            columnContent = formInputCurrentValue.name
          }
          break
        case FIELD_TYPE.multiSelect:
          comma = ''
          for (let choice of formInputCurrentValue) {
            columnContent += `${comma}${choice.name}`
            comma = ', '
          }
          break
        case FIELD_TYPE.file:
          columnContent = <FileLink fileName={formInputCurrentValue} jwt={fileJwt} />
          break
        case FIELD_TYPE.image:
          columnContent = <FileLink fileName={formInputCurrentValue} />
          break
        case FIELD_TYPE.yesNo:
          columnContent = formInputCurrentValue === 'Y' ? 'Yes' : 'No'
          break
        case FIELD_TYPE.currency:
          const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
          columnContent = formatter.format(formInputCurrentValue)
          break
        case FIELD_TYPE.referenceList:
          columnContent = 'WARNING: Reference list not allowed here'
          //columnContent = <ReferenceList formInputs={formInputCurrentValue} objectId={gridRow[0]} field={detailField} action={parentAction} />
          break
        case FIELD_TYPE.date:
          // make sure the date is in mm-dd-yyyy format
          if (formInputCurrentValue[4] === '-' && formInputCurrentValue[7] === '-') {
            columnContent = `${formInputCurrentValue.substr(5, 5)}-${formInputCurrentValue.substr(0, 4)}`
          }
          break
        default:
          columnContent = formInputCurrentValue
      }
    }

    //------------ FORMAT HOOK CODE ---------------------
    if (detailField.formatType && makeInteger(detailField.formatType.id) === FORMAT_TYPE.useHook) {
      console.log(`**************** RefListRow field "${detailField.name}" has format hook "${detailField.formatValue}"`)
      const hookInfo = getHookInfo(detailField.formatValue)
      columnContent = allHooks[hookInfo.moduleName][hookInfo.functionName](rowObject, detailField, gridRow, columnContent)
    }

    //------------ FORMAT HOOK CODE ---------------------

    // add the column to the row
    
    rowColumns.push(
      <td className={className} key={columnIndex}>
        {columnContent}
      </td>)
  } // for column in gridRow

  let className = null
  if (deleted) {
    className = 'ref-list-row-deleted-item'
  }
  
  let buttonColumn = ''
  if (!printView) {
    buttonColumn = <td style={{ whiteSpace: 'nowrap' }}>{buttons}</td>
  }

  const tooltip = showObjectId === 'Y' ? <UncontrolledTooltip autohide={false} placement="left" target={`ID${refListObjectId}`}>{`ID: ${refListObjectId}`}</UncontrolledTooltip> : ''
  let component = 
    <>
      <tr id={`ID${refListObjectId}`} className={className}>
        {buttonColumn}
        {rowColumns}
      </tr>
      {tooltip}
    </>  
  // logic for if the row is open without clicking the edit button and an action is done by the parent
  if (showObject) {
    if (!parentAction && rowAction) {
      setRowAction(null)
    }
    component =
      <tr>
        <td colSpan={colSpan + 1}>
          <AppObject object={getObjectFromList()} action={rowAction} parentObjectId={parentObjectId} existingFormInput={formInputs[refListObjectId]} />
        </td>
      </tr>
  }

  if (graphQLErrors.length > 0) {
    component = <Alert color="danger">{graphQLErrors[0].message}</Alert>
  }

  return component
}